import type { MorphemeGlyphTileWordMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import {
  MorphemeGlyphTileUi,
  type MorphemeGlyphTileUiProps,
} from "#morpheme-glyph-tile-ui";
import { useMorphemeGlyphTileWordMorphemeSlice } from "#use-morpheme-glyph-tile-grapheme-morpheme-slice";

export type WordMorphemeGlyphTileUiProps = {
  /**
   * Pre-resolved word slice from a batched parent read (`useBatchedWordTileData`). When supplied
   * the tile calls NO data hooks; otherwise it resolves its own per-character pinyin queries.
   */
  morphemeGlyphTileSlice?: MorphemeGlyphTileWordMorphemeSlice;
  word: string;
} & Omit<MorphemeGlyphTileUiProps, "morphemeGlyphTileSlice">;

function SelfResolvingWordMorphemeGlyphTileUi({
  word,
  ...morphemeGlyphTileProps
}: Omit<WordMorphemeGlyphTileUiProps, "morphemeGlyphTileSlice">) {
  const morphemeGlyphTileSlice = useMorphemeGlyphTileWordMorphemeSlice(word);
  return (
    <MorphemeGlyphTileUi
      morphemeGlyphTileSlice={morphemeGlyphTileSlice}
      {...morphemeGlyphTileProps}
    />
  );
}

/**
 * A word glyph tile. Its pinyin slice is either resolved for it by a batched parent read
 * (the fast path for grids of many tiles — zero per-tile queries) or resolved by the tile itself.
 * The choice is made once here by prop presence.
 */
export function WordMorphemeGlyphTileUi({
  morphemeGlyphTileSlice,
  word,
  ...morphemeGlyphTileProps
}: WordMorphemeGlyphTileUiProps) {
  if (morphemeGlyphTileSlice !== undefined) {
    return (
      <MorphemeGlyphTileUi
        morphemeGlyphTileSlice={morphemeGlyphTileSlice}
        {...morphemeGlyphTileProps}
      />
    );
  }
  return <SelfResolvingWordMorphemeGlyphTileUi word={word} {...morphemeGlyphTileProps} />;
}
